import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { get, post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { dateTime, humanize } from '@/lib/utils';
import { DataTable, PageHeader } from '@/components/ui/layout';
import { Alert, Badge, Button } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';

interface ProposedChange {
  from: string | null;
  to: string | null;
}

interface ProposalRow {
  id: string;
  personId: string;
  uhid: string;
  masterName: string;
  facilityId: string;
  facilityName: string | null;
  patientId: string;
  changes: Record<string, ProposedChange>;
  proposedBy: string | null;
  proposedAt: string;
}

const FIELD_LABELS: Record<string, string> = {
  fullName: 'Name',
  sex: 'Sex',
  dob: 'Date of birth',
  cnic: 'CNIC',
  phone: 'Mobile',
  altPhone: 'Other mobile',
  email: 'Email',
};

export function PersonChangesPage() {
  const toast = useToast();
  const [revealed, setRevealed] = useState<Record<string, ProposalRow>>({});
  const changes = useQuery({
    queryKey: ['person-changes'],
    queryFn: () => get<{ data: ProposalRow[]; nextCursor: string | null }>('/platform/person-changes?limit=100'),
  });
  const reveal = useMutation({
    mutationFn: (id: string) => post<ProposalRow>(`/platform/person-changes/${id}/reveal`),
    onSuccess: (row) => setRevealed((current) => ({ ...current, [row.id]: row })),
    onError: (error) => toast.error('Could not show the details', describeError(error)),
  });
  const rows = changes.data?.data.map((row) => revealed[row.id] ?? row);

  return (
    <>
      <PageHeader
        title="Patient details changed at a facility"
        description="What a facility typed, waiting on the patient to accept it"
      />

      <Alert className="mb-4">
        A facility that corrects a patient’s details changes only its own copy. The patient sees it on their own
        account, and the platform record moves only when they accept. Nothing here needs your action — this is
        the list of what is outstanding. Names and values are masked; showing one is recorded in the audit
        trail.
      </Alert>

      <DataTable
        rows={rows}
        loading={changes.isLoading} refreshing={changes.isFetching}
        error={changes.error}
        rowKey={(row) => row.id}
        empty="No facility has changed a patient’s details."
        columns={[
          {
            header: 'Patient',
            sort: (row) => row.masterName,
            cell: (row) => (
              <div>
                <p className="font-medium">{row.masterName}</p>
                <p className="text-xs text-muted-foreground">{row.uhid}</p>
              </div>
            ),
          },
          { header: 'Facility', sort: (row) => row.facilityName, cell: (row) => row.facilityName ?? '—' },
          {
            header: 'What changed',
            cell: (row) => (
              <ul className="space-y-0.5">
                {Object.entries(row.changes).map(([field, change]) => (
                  <li key={field} className="text-sm">
                    <span className="text-muted-foreground">{FIELD_LABELS[field] ?? humanize(field)}: </span>
                    <span className="line-through opacity-60">{change.from ?? '—'}</span>{' '}
                    <span className="font-medium">› {change.to ?? '—'}</span>
                  </li>
                ))}
              </ul>
            ),
          },
          { header: 'Typed by', sort: (row) => row.proposedBy, cell: (row) => row.proposedBy ?? '—' },
          { header: 'When', sort: (row) => row.proposedAt, cell: (row) => dateTime(row.proposedAt) },
          { header: 'Status', cell: () => <Badge variant="warning">Waiting on the patient</Badge> },
          {
            header: '',
            align: 'right',
            cell: (row) =>
              revealed[row.id] ? (
                <Badge variant="muted">Shown · audited</Badge>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  loading={reveal.isPending && reveal.variables === row.id}
                  onClick={() => reveal.mutate(row.id)}
                >
                  Show details
                </Button>
              ),
          },
        ]}
      />
    </>
  );
}
